import { supabase } from '../lib/supabase'
import { extractUserNames } from '../utils/userMeta'
import logger from '../utils/logger'

export async function fetchMatches() {
  const { data: { user } } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      match_players (
        *,
        player:players (*)
      ),
      games (id, status, session_group_id),
      sessions (id, status)
    `)
    .order('created_at', { ascending: false })

  if (error) throw error

  // Filter to only matches the current user owns or is a member of.
  // RLS returns all visible matches (including followed players' matches),
  // but the home/history pages should only show the user's own matches.
  return data.filter(m =>
    m.owner_id === user.id ||
    m.match_players?.some(mp => mp.user_id === user.id)
  )
}

export async function fetchActivityFeed(followedPlayerIds = new Set()) {
  const { data: { user } } = await supabase.auth.getUser()
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      match_players (
        *,
        player:players (*)
      ),
      games (id, status, session_group_id, completed_at),
      sessions (id, status)
    `)
    .in('status', ['active', 'completed'])
    .order('created_at', { ascending: false })
    .limit(50)

  if (error) throw error

  // Defense-in-depth: only show matches the user owns, is a member of,
  // or where a followed player participates.
  // Hide stale active matches — likely abandoned.
  // Use the most recent activity timestamp (last game completion, or created_at as fallback).
  // If the last activity was >24h ago and nothing is in progress, hide it.
  const staleThreshold = Date.now() - 24 * 60 * 60 * 1000
  const filtered = data.filter(m => {
    if (m.status === 'active') {
      const hasLiveSession = m.sessions?.some(s => s.status === 'in_progress')
      const completionTimes = (m.games || []).filter(g => g.completed_at).map(g => new Date(g.completed_at).getTime())
      const lastActivity = completionTimes.length > 0
        ? Math.max(...completionTimes)
        : new Date(m.created_at).getTime()
      if (!hasLiveSession && lastActivity < staleThreshold) return false
    }
    return (
      m.owner_id === user.id ||
      m.match_players?.some(mp => mp.user_id === user.id) ||
      m.match_players?.some(mp => followedPlayerIds.has(mp.player_id))
    )
  })

  // Live rounds first, then completed — each group sorted by created_at desc
  filtered.sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1
    if (a.status !== 'active' && b.status === 'active') return 1
    return new Date(b.created_at) - new Date(a.created_at)
  })

  return filtered.slice(0, 20)
}

export async function fetchMatch(id) {
  const { data, error } = await supabase
    .from('matches')
    .select(`
      *,
      match_players (
        *,
        player:players (*)
      ),
      match_teams (
        *,
        match_team_players (
          *,
          player:players (*)
        )
      ),
      sessions (
        *,
        session_groups (
          *,
          session_group_teams (
            *,
            match_team:match_teams (id, team_name)
          )
        )
      ),
      games (
        *,
        game_players (
          *,
          player:players (*)
        ),
        scores (*),
        teams (
          *,
          team_players (
            *,
            player:players (*)
          )
        ),
        ctp_results (*)
      )
    `)
    .eq('id', id)
    .maybeSingle()

  if (error) throw error
  if (!data) throw new Error('Match not found or access denied')
  return data
}

export async function createMatch({ match_name, match_type = 'trip', status = 'setup' }) {
  const { data: { user } } = await supabase.auth.getUser()

  // Insert and fetch separately — .insert().select() fails because the
  // can_view_match() RLS policy can't see the new row within the same statement.
  const { error: insertError } = await supabase
    .from('matches')
    .insert({
      match_name,
      match_type,
      owner_id: user.id,
      status,
    })

  if (insertError) throw insertError

  const { data, error } = await supabase
    .from('matches')
    .select()
    .eq('owner_id', user.id)
    .order('created_at', { ascending: false })
    .limit(1)
    .single()

  if (error) throw error

  // Auto-add the match owner as a player/scorer
  try {
    // Find or create a player record for the owner
    let { data: player } = await supabase
      .from('players')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle()

    if (!player) {
      const { firstName, lastName, displayName } = extractUserNames(user)
      const { data: newPlayer } = await supabase
        .from('players')
        .insert({
          first_name: firstName,
          last_name: lastName,
          display_name: displayName,
          email: user.email,
          avatar_color: '#0ea47a',
          created_by: user.id,
          user_id: user.id,
        })
        .select()
        .single()
      player = newPlayer
    }

    if (player) {
      await supabase
        .from('match_players')
        .insert({
          match_id: data.id,
          player_id: player.id,
          user_id: user.id,
          handicap_index: player.handicap_index ?? 0,
          is_scorer: true,
        })
    }
  } catch (e) {
    // Don't fail match creation if auto-add fails
    logger.warn('Auto-add owner to match failed: ' + e?.message)
  }

  return data
}

export async function updateMatch(id, updates) {
  const { data, error } = await supabase
    .from('matches')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deleteMatch(id) {
  const { error } = await supabase
    .from('matches')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export async function addMatchPlayer({ match_id, player_id, user_id, handicap_index, is_scorer }) {
  // Look up the player record for user_id and default handicap
  const { data: player } = await supabase
    .from('players')
    .select('user_id, handicap_index, gender')
    .eq('id', player_id)
    .single()

  const resolvedUserId = user_id || player?.user_id || null
  const resolvedHandicap = handicap_index ?? player?.handicap_index ?? 0

  const { data, error } = await supabase
    .from('match_players')
    .insert({
      match_id,
      player_id,
      user_id: resolvedUserId,
      handicap_index: resolvedHandicap,
      gender: player?.gender || 'M',
      is_scorer: is_scorer || false,
    })
    .select(`
      *,
      player:players (*)
    `)
    .single()

  if (error) throw error
  return data
}

export async function updateMatchPlayer(id, updates) {
  const { data, error } = await supabase
    .from('match_players')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      player:players (*)
    `)
    .single()

  if (error) throw error
  return data
}

export async function removeMatchPlayer(id) {
  const { error } = await supabase
    .from('match_players')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ── Match Teams ──

export async function createMatchTeam({ match_id, team_name, team_order }) {
  const { data, error } = await supabase
    .from('match_teams')
    .insert({ match_id, team_name, team_order })
    .select(`*, match_team_players (*, player:players (*))`)
    .single()

  if (error) throw error
  return data
}

export async function updateMatchTeam(id, updates) {
  const { data, error } = await supabase
    .from('match_teams')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deleteMatchTeam(id) {
  const { error } = await supabase
    .from('match_teams')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export async function addMatchTeamPlayer({ match_team_id, player_id }) {
  const { data, error } = await supabase
    .from('match_team_players')
    .insert({ match_team_id, player_id })
    .select(`*, player:players (*)`)
    .single()

  if (error) throw error
  return data
}

export async function removeMatchTeamPlayer(id) {
  const { error } = await supabase
    .from('match_team_players')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// ── Sessions ──

export async function createSession({ match_id, session_name, session_order, game_type, game_config, course_name, course_id, tee_name, num_holes, press_config, wager, groups, matchTeams }) {
  // 1. Insert session row
  const { data: session, error: sessionError } = await supabase
    .from('sessions')
    .insert({
      match_id,
      session_name,
      session_order,
      game_type,
      game_config,
      course_name,
      course_id,
      tee_name,
      num_holes,
      press_config,
      wager,
    })
    .select()
    .single()

  if (sessionError) throw sessionError

  // 2. Insert session_groups rows
  for (const group of groups) {
    const { data: sessionGroup, error: groupError } = await supabase
      .from('session_groups')
      .insert({
        session_id: session.id,
        group_name: group.name,
        group_order: group.order,
      })
      .select()
      .single()

    if (groupError) throw groupError

    // 3. Derive which match_teams are represented in this group and insert session_group_teams
    const teamIdsInGroup = new Set()
    for (const playerId of group.playerIds) {
      const team = matchTeams.find(t =>
        (t.match_team_players || []).some(mtp => mtp.player_id === playerId)
      )
      if (team) teamIdsInGroup.add(team.id)
    }

    for (const matchTeamId of teamIdsInGroup) {
      const { error: sgtError } = await supabase
        .from('session_group_teams')
        .insert({
          session_group_id: sessionGroup.id,
          match_team_id: matchTeamId,
        })

      if (sgtError) throw sgtError
    }
  }

  return session
}

export async function deleteSession(id) {
  const { error } = await supabase
    .from('sessions')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export async function activateSession({ id, match_id }) {
  const { data: { user } } = await supabase.auth.getUser()

  // Check if this session is already active (for idempotent re-activation)
  const { data: activeSessions, error: checkError } = await supabase
    .from('sessions')
    .select('id')
    .eq('match_id', match_id)
    .eq('status', 'in_progress')

  if (checkError) throw checkError
  const isAlreadyActive = activeSessions.some(s => s.id === id)

  // Fetch full session row
  const { data: session, error: sessionError } = await supabase
    .from('sessions')
    .select('*')
    .eq('id', id)
    .single()

  if (sessionError) throw sessionError

  // Fetch session_groups ordered by group_order (include session_group_teams for fallback player lookup)
  const { data: groups, error: groupsError } = await supabase
    .from('session_groups')
    .select('*, session_group_teams (*, match_team:match_teams (id))')
    .eq('session_id', id)
    .order('group_order')

  if (groupsError) throw groupsError

  const groupIds = groups.map(g => g.id)

  // RESUME PATH: If session was completed, reopen existing games (preserves scores)
  if (session.status === 'completed' && groupIds.length > 0) {
    // Find existing completed games linked to these groups
    const { data: existingGames } = await supabase
      .from('games')
      .select('id, session_group_id')
      .in('session_group_id', groupIds)
      .eq('status', 'completed')

    if (existingGames?.length > 0) {
      // Flip games back to in_progress and delete their settlements
      for (const eg of existingGames) {
        await supabase.from('settlements').delete().eq('game_id', eg.id)
        await supabase.from('games')
          .update({ status: 'in_progress', completed_at: null })
          .eq('id', eg.id)
      }

      // Re-link session_groups.game_id where missing
      for (const group of groups) {
        const game = existingGames.find(g => g.session_group_id === group.id)
        if (game && group.game_id !== game.id) {
          await supabase.from('session_groups').update({ game_id: game.id }).eq('id', group.id)
        }
      }

      // Update session status to in_progress
      const { data, error } = await supabase
        .from('sessions')
        .update({ status: 'in_progress' })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    }
    // If no existing games found, fall through to fresh creation path below
  }

  // FRESH ACTIVATION PATH: Create new games for setup sessions or sessions with no existing games

  // Fetch match_teams with their players
  const { data: matchTeams, error: teamsError } = await supabase
    .from('match_teams')
    .select('*, match_team_players (*, player:players (*))')
    .eq('match_id', match_id)

  if (teamsError) throw teamsError

  let groupAssignments = session.game_config?.groupPlayerAssignments || []

  // Fallback: derive player assignments from session_group_teams + match_team_players
  if (groupAssignments.length === 0) {
    const matchTeamMap = new Map(matchTeams.map(mt => [mt.id, mt]))
    groupAssignments = groups.map(g => {
      const teamLinks = g.session_group_teams || []
      const playerIds = []
      for (const sgt of teamLinks) {
        const teamId = sgt.match_team?.id || sgt.match_team_id
        const matchTeam = matchTeamMap.get(teamId)
        if (matchTeam) {
          for (const mtp of (matchTeam.match_team_players || [])) {
            playerIds.push(mtp.player_id)
          }
        }
      }
      return { groupOrder: g.group_order, playerIds }
    }).filter(a => a.playerIds.length > 0)
  }

  // Clean up any orphaned completed/canceled games from previous activations
  if (groupIds.length > 0) {
    const { data: orphanedGames } = await supabase
      .from('games')
      .select('id')
      .in('session_group_id', groupIds)
      .in('status', ['completed', 'canceled'])
    if (orphanedGames?.length > 0) {
      for (const og of orphanedGames) {
        await supabase.from('settlements').delete().eq('game_id', og.id)
        await supabase.from('ctp_results').delete().eq('game_id', og.id)
        await supabase.from('side_bets').delete().eq('game_id', og.id)
        await supabase.from('bets').delete().eq('game_id', og.id)
        await supabase.from('scores').delete().eq('game_id', og.id)
        const { data: teamRows } = await supabase.from('teams').select('id').eq('game_id', og.id)
        if (teamRows?.length > 0) {
          for (const t of teamRows) await supabase.from('team_players').delete().eq('team_id', t.id)
          await supabase.from('teams').delete().eq('game_id', og.id)
        }
        await supabase.from('game_players').delete().eq('game_id', og.id)
        await supabase.from('games').delete().eq('id', og.id)
      }
      // Clear stale game_id on groups that pointed to deleted games
      const deletedIds = new Set(orphanedGames.map(og => og.id))
      for (const group of groups) {
        if (group.game_id && deletedIds.has(group.game_id)) {
          await supabase.from('session_groups').update({ game_id: null }).eq('id', group.id)
          group.game_id = null
        }
      }
    }
  }

  // Create a game for each session_group (skip groups that already have a game)
  for (const group of groups) {
    if (group.game_id) continue

    const assignment = groupAssignments.find(a => a.groupOrder === group.group_order)
    const playerIds = assignment?.playerIds || []
    if (playerIds.length === 0) continue

    // Build per-game game_config from session
    const gameConfig = { ...(session.game_config || {}) }
    delete gameConfig.groupPlayerAssignments
    delete gameConfig.scorerPlayerIds
    if (session.press_config && Object.keys(session.press_config).length > 0) {
      gameConfig.pressConfig = session.press_config
    }

    const { data: game, error: gameError } = await supabase
      .from('games')
      .insert({
        created_by: user.id,
        match_id,
        game_name: group.group_name,
        game_type: session.game_type || 'stroke',
        game_config: gameConfig,
        course_name: session.course_name || null,
        course_id: session.course_id || null,
        tee_name: session.tee_name || null,
        wager: session.wager || 0,
        num_holes: session.num_holes || 18,
        session_group_id: group.id,
        status: 'in_progress',
        started_at: new Date().toISOString(),
      })
      .select()
      .single()

    if (gameError) throw gameError

    // Insert game_players (with per-session scorer designations from game_config)
    const sessionScorerIds = session.game_config?.scorerPlayerIds || []
    const { error: gpError } = await supabase
      .from('game_players')
      .insert(playerIds.map((player_id, index) => ({
        game_id: game.id,
        player_id,
        position: index + 1,
        is_scorer: sessionScorerIds.includes(player_id),
      })))
    if (gpError) throw gpError

    // Build per-game teams from match_teams
    const playerIdSet = new Set(playerIds)
    for (const matchTeam of matchTeams) {
      const teamPlayerIds = (matchTeam.match_team_players || [])
        .filter(mtp => playerIdSet.has(mtp.player_id))
        .map(mtp => mtp.player_id)
      if (teamPlayerIds.length === 0) continue

      const { data: teamRow, error: teamError } = await supabase
        .from('teams')
        .insert({ game_id: game.id, team_name: matchTeam.team_name, team_order: matchTeam.team_order })
        .select()
        .single()
      if (teamError) throw teamError

      const { error: tpError } = await supabase
        .from('team_players')
        .insert(teamPlayerIds.map(player_id => ({ team_id: teamRow.id, player_id })))
      if (tpError) throw tpError
    }

    // Link session_group back to the game
    await supabase.from('session_groups').update({ game_id: game.id }).eq('id', group.id)

    // Create side_bets rows from session config
    const sideBetsConfig = session.game_config?.sideBets || {}
    if (sideBetsConfig.ctp?.enabled) {
      await supabase.from('side_bets').insert({ match_id, game_id: game.id, bet_type: 'ctp', amount: sideBetsConfig.ctp.amount || 5, scope: 'round' })
    }
    if (sideBetsConfig.lowNet?.enabled) {
      await supabase.from('side_bets').insert({ match_id, game_id: game.id, bet_type: 'low_net', amount: sideBetsConfig.lowNet.amount || 10, scope: 'round' })
    }
    if (sideBetsConfig.skins?.enabled) {
      await supabase.from('side_bets').insert({
        match_id, game_id: game.id,
        bet_type: sideBetsConfig.skins.carryOver ? 'skins_carry' : 'skins',
        amount: sideBetsConfig.skins.amount || 5, scope: 'round',
        ...(sideBetsConfig.skins.grossTiebreaker ? { config: { gross_tiebreaker: true } } : {}),
      })
    }
  }

  // Update session status to in_progress (skip if already active)
  if (!isAlreadyActive) {
    const { data, error } = await supabase
      .from('sessions')
      .update({ status: 'in_progress' })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Return existing session data for already-active sessions
  return session
}

export async function deactivateSession({ id, match_id, mode = 'complete' }) {
  // Find all games linked to this session's groups
  const { data: groups, error: groupsError } = await supabase
    .from('session_groups')
    .select('id, game_id')
    .eq('session_id', id)

  if (groupsError) throw groupsError

  // Find game IDs from session_group.game_id links
  let gameIds = groups.map(g => g.game_id).filter(Boolean)

  // Fallback: also find games by session_group_id (in case game_id links were cleared)
  if (gameIds.length === 0 && groups.length > 0) {
    const groupIds = groups.map(g => g.id)
    const { data: linkedGames } = await supabase
      .from('games')
      .select('id')
      .in('session_group_id', groupIds)
      .eq('status', 'in_progress')
    if (linkedGames?.length > 0) {
      gameIds = linkedGames.map(g => g.id)
    }
  }

  if (mode === 'discard') {
    // Delete all games (cascades to scores, game_players, teams, etc.)
    for (const gameId of gameIds) {
      // Delete settlements first (may not cascade)
      await supabase.from('settlements').delete().eq('game_id', gameId)
      const { error } = await supabase.from('games').delete().eq('id', gameId)
      if (error) throw error
    }
  } else {
    // Complete all in_progress games
    for (const gameId of gameIds) {
      const { error: gameUpdateError } = await supabase
        .from('games')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', gameId)
        .eq('status', 'in_progress')
      if (gameUpdateError) throw gameUpdateError
    }
  }

  // Clear game_id links on session_groups
  for (const group of groups) {
    if (group.game_id) {
      await supabase
        .from('session_groups')
        .update({ game_id: null })
        .eq('id', group.id)
    }
  }

  // Set session status: 'completed' when finishing, 'setup' when discarding
  const { data, error } = await supabase
    .from('sessions')
    .update({ status: mode === 'discard' ? 'setup' : 'completed' })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function updateSession({ id, match_id, session_name, game_type, game_config, course_name, course_id, tee_name, num_holes, press_config, wager, groups, matchTeams }) {
  // 1. Update session row
  const { data: session, error: sessionError } = await supabase
    .from('sessions')
    .update({
      session_name,
      game_type,
      game_config,
      course_name,
      course_id,
      tee_name,
      num_holes,
      press_config,
      wager,
    })
    .eq('id', id)
    .select()
    .single()

  if (sessionError) throw sessionError

  // 2. Delete existing session_groups (cascade deletes session_group_teams)
  const { error: deleteGroupsError } = await supabase
    .from('session_groups')
    .delete()
    .eq('session_id', id)

  if (deleteGroupsError) throw deleteGroupsError

  // 3. Re-insert session_groups and session_group_teams
  for (const group of groups) {
    const { data: sessionGroup, error: groupError } = await supabase
      .from('session_groups')
      .insert({
        session_id: id,
        group_name: group.name,
        group_order: group.order,
      })
      .select()
      .single()

    if (groupError) throw groupError

    const teamIdsInGroup = new Set()
    for (const playerId of group.playerIds) {
      const team = matchTeams.find(t =>
        (t.match_team_players || []).some(mtp => mtp.player_id === playerId)
      )
      if (team) teamIdsInGroup.add(team.id)
    }

    for (const matchTeamId of teamIdsInGroup) {
      const { error: sgtError } = await supabase
        .from('session_group_teams')
        .insert({
          session_group_id: sessionGroup.id,
          match_team_id: matchTeamId,
        })

      if (sgtError) throw sgtError
    }
  }

  return session
}

export async function reorderSessions(updates) {
  // updates: [{ id, session_order }]
  for (const { id, session_order } of updates) {
    const { error } = await supabase
      .from('sessions')
      .update({ session_order })
      .eq('id', id)

    if (error) throw error
  }
}
